import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TableModel } from '../model/table';
import { PieceFace } from '../bricks/Piece';
import { useViews } from '../scene/ViewContext';
import { anchorRect, hoveredBoardObject, ANCHORS_CHANGED } from '../boards/runtime/semantic';
import { PublicProgress } from '../components/PublicProgress';
import { CatalogMetadata } from '../components/CatalogMetadata';
import './inspect-details.css';
import { usePresentation } from '../presentation/PresentationContext';
import { fontStack } from '../theme/themeV2';
import { withinPreviewBridge } from './previewBridge';
import { detailsLabelFor } from './detailsLabel';

/** Temporarily inspects observed cards only, without activating a target or changing a draft. */
export function AltHoverCard({ table }: { table: TableModel }) {
  const views = useViews();
  const { presentation } = usePresentation();
  const bodyFont = fontStack(presentation && 'format' in presentation ? presentation.theme.fonts?.body : undefined, 'system-ui, sans-serif');
  const [anchor, setAnchor] = useState<{ element: HTMLElement; id: string; boardAnchor?: string; details?: boolean; placement?: 'right' } | null>(null);
  const [position, setPosition] = useState({ left: 8, top: 8, maxHeight: 400 });
  const ref = useRef<HTMLElement>(null);
  // Where a card was last chosen. The pointer rests there while the table redraws (the browser repeats its move), and that must not
  // open the preview again; it ends when the pointer actually moves.
  const chosenAt = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    setAnchor(null);
    let hovered: typeof anchor = null, alt = false, touch = false;
    const show = () => setAnchor(previous => { const next = (alt || hovered?.details) && hovered?.element.isConnected ? hovered : null; return previous?.id === next?.id && previous?.element === next?.element ? previous : next; });
    const pointer = (event: PointerEvent) => {
      if (chosenAt.current) {
        if (Math.hypot(event.clientX - chosenAt.current.x, event.clientY - chosenAt.current.y) < 4) return;
        chosenAt.current = null;
      }
      const target = event.target instanceof Element ? event.target : null, board = hoveredBoardObject(target), element = target?.closest<HTMLElement>('[data-inspect-id]');
      if (ref.current && hovered?.element.isConnected && (target && ref.current.contains(target) || withinPreviewBridge(event.clientX, event.clientY, hovered.element.getBoundingClientRect(), ref.current.getBoundingClientRect()))) {
        // Native scroll boundary events can omit Alt while the key remains held.
        if (event.altKey) alt = true;
        show(); return;
      }
      touch = event.pointerType === 'touch';
      hovered = touch ? null : board?.objectId && target instanceof HTMLElement ? { element: target, id: board.objectId, boardAnchor: board.anchor } : element ? { element, id: element.dataset.inspectId!, details: element.dataset.hoverDetails === 'true', placement: element.dataset.previewPlacement === 'right' ? 'right' : undefined } : null;
      alt = event.altKey; show();
    };
    const keyDown = (event: KeyboardEvent) => {
      if (event.key === 'Alt') { alt = true; show(); }
      if (event.key === 'Escape' && (alt || hovered?.details) && hovered) {
        event.preventDefault(); event.stopImmediatePropagation(); hovered = null; setAnchor(null);
      }
    };
    const keyUp = (event: KeyboardEvent) => { if (event.key === 'Alt' || !event.altKey) { alt = false; show(); } };
    const focus = (event: FocusEvent) => {
      const element = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-hover-details="true"]') : null;
      hovered = element && !touch ? { element, id: element.dataset.inspectId!, details: true, placement: element.dataset.previewPlacement === 'right' ? 'right' : undefined } : null;
      show();
    };
    const clear = () => { alt = false; hovered = null; setAnchor(null); };
    // Choosing a card (click or tap) ends the glance at it: the preview must not stay open over the table the choice returns to.
    const select = (event: MouseEvent) => {
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      if (event.detail > 0) chosenAt.current = { x: event.clientX, y: event.clientY };
      clear();
    };
    const scroll = (event: Event) => {
      // Reading within the preview does not change its observed object or anchor.
      if (event.target instanceof Node && ref.current?.contains(event.target)) return;
      clear();
    };
    const visibility = () => { if (document.hidden) clear(); };
    window.addEventListener('pointerover', pointer);
    window.addEventListener('pointermove', pointer);
    window.addEventListener('keydown', keyDown, true);
    window.addEventListener('keyup', keyUp, true);
    document.addEventListener('focusin', focus);
    window.addEventListener('blur', clear);
    window.addEventListener('resize', clear);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('click', select, true);
    window.addEventListener(ANCHORS_CHANGED, clear);
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('pointerleave', clear);
    document.addEventListener('fullscreenchange', clear);
    return () => {
      window.removeEventListener('pointerover', pointer);
      window.removeEventListener('pointermove', pointer);
      window.removeEventListener('keydown', keyDown, true);
      window.removeEventListener('keyup', keyUp, true);
      document.removeEventListener('focusin', focus);
      window.removeEventListener('blur', clear);
      window.removeEventListener('resize', clear);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('click', select, true);
      window.removeEventListener(ANCHORS_CHANGED, clear);
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('pointerleave', clear);
      document.removeEventListener('fullscreenchange', clear);
    };
  }, [table, views?.current]);
  const object = anchor ? table.objects.get(anchor.id) : undefined;
  // An object in a zone only its owner can see (the cards a pick offers, a hand) has no public details.
  const detailsLabel = detailsLabelFor(table, object);
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const box = (anchor.boardAnchor ? anchorRect(anchor.boardAnchor) : null) ?? anchor.element.getBoundingClientRect(), panel = ref.current.getBoundingClientRect();
    if (anchor.placement === 'right') {
      const hand = anchor.element.closest('.scene-root')?.querySelector<HTMLElement>('.scene-area-hand')?.getBoundingClientRect();
      const floor = Math.max(120, hand?.top ?? window.innerHeight) - 8;
      setPosition({ left: Math.max(8, window.innerWidth - panel.width - 8), top: 8, maxHeight: Math.max(80, floor - 16) });
      return;
    }
    const bar = document.querySelector('.action-bar')?.getBoundingClientRect();
    const header = document.querySelector('.action-bar.is-header');
    const floor = !header && bar ? Math.min(window.innerHeight - 8, bar.top - 8) : window.innerHeight - 8;
    let left = box.right + 12;
    if (left + panel.width > window.innerWidth - 8) left = box.left - panel.width - 12;
    setPosition({ left: Math.max(8, Math.min(left, window.innerWidth - panel.width - 8)),
      top: Math.max(8, Math.min(box.top, floor - panel.height)), maxHeight: Math.max(80, window.innerHeight - 100) });
  }, [anchor, object]);
  if (!object || object.faceDown || !anchor) return null;
  // A fullscreen element paints only its own subtree. Keep viewport coordinates,
  // and preserve the normal body portal when this observed target is windowed.
  const fullscreen = document.fullscreenElement;
  const portalHost = fullscreen?.contains(anchor.element) ? fullscreen : document.body;
  return createPortal(<aside ref={ref} className="alt-hover-card" role="tooltip" aria-label={`Preview: ${object.title}`}
    style={{ ...position, fontFamily: bodyFont }}>
    <div className="inspect-face"><PieceFace object={object} zoneKind={null} width={320} /></div>
    <div className="inspect-prose"><strong>{object.title}</strong>
      {object.catalog?.text && <p>{object.catalog.text}</p>}
      <CatalogMetadata object={object} />
      <PublicProgress object={object} table={table} />
      <small>{anchor.details ? `${detailsLabel} · Press I or hold on touch to inspect` :'Hold Alt to inspect · Release to return'}</small>
    </div>
  </aside>, portalHost);
}
